import * as React from 'react';
import {
  Checkbox as ChakraCheckbox,
  type CheckboxRootProps,
} from '@chakra-ui/react';

/**
 * Project Checkbox.
 *
 * NB-shaped facade over Chakra v3's `Checkbox` compositional API.
 * Consumers keep using `<Checkbox isChecked onChange>{label}</Checkbox>`;
 * the wrapper renders the `HiddenInput` + `Control` + optional `Label`
 * internally and bridges:
 *
 *   - NB `isChecked` -> Chakra `checked`
 *   - NB `onChange(checked: boolean)` -> Chakra `onCheckedChange(details)`
 *   - NB `isDisabled` -> Chakra `disabled`
 */

export type CheckboxProps = Omit<
  CheckboxRootProps,
  'checked' | 'onCheckedChange' | 'onChange'
> & {
  isChecked?: boolean;
  onChange?: (checked: boolean) => void;
  isDisabled?: boolean;
  children?: React.ReactNode;
};

export const Checkbox = React.forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      isChecked,
      onChange,
      isDisabled,
      disabled,
      children,
      colorPalette = 'primary',
      // Pull box-visual styling props out so they land on Control (where
      // the consumer meant) rather than doubling the border on Root.
      borderWidth,
      borderColor,
      borderRadius,
      borderStyle,
      rounded,
      backgroundColor,
      bgColor,
      bg,
      _checked,
      ...rootProps
    },
    ref
  ) => {
    const controlProps = {
      borderWidth,
      borderColor,
      borderRadius,
      borderStyle,
      rounded,
      backgroundColor,
      bgColor,
      bg,
      _checked,
    };

    return (
      <ChakraCheckbox.Root
        ref={ref}
        checked={isChecked}
        onCheckedChange={(e) => onChange?.(!!e.checked)}
        disabled={disabled ?? isDisabled}
        colorPalette={colorPalette}
        {...rootProps}
      >
        <ChakraCheckbox.HiddenInput />
        <ChakraCheckbox.Control {...controlProps} />
        {children !== undefined && (
          <ChakraCheckbox.Label>{children}</ChakraCheckbox.Label>
        )}
      </ChakraCheckbox.Root>
    );
  }
);
Checkbox.displayName = 'Checkbox';
